/**
 * Reports page — PDF export.
 *
 * Scope, per explicit request: ONLY the risk register's own numbers, shown twice — once from
 * the inherent perspective, once from the residual perspective — as real charts (bar charts, a
 * colored heat map grid), matching the look of the on-screen cards rather than plain tables.
 * No per-risk detail (that was tried and explicitly rejected), and no Control/Compliance/
 * Issues/Gap Analysis content — this export is risk-overview only.
 *
 * Built on the same `Doc` engine every other register export already uses (see pdfDoc.ts):
 * programmatic, paginated, real selectable text, not a screenshot. The chart primitives
 * (statRow/barChart/heatmapGrid) live on Doc itself, additive to the two other exports sharing
 * that file (aiUseCasePdf.ts, meetingMinutesPdf.ts).
 */
import { Doc, type PdfBuildResult } from './pdfDoc';

const FOOTER_LABEL = 'Mercell Nimbus — Reports';

export interface Breakdown {
  labels: string[];
  values: number[];
}

export interface RiskPerspectiveData {
  total: number;
  critical: number;
  avgScore: string | number;
  /** Only meaningful for the inherent perspective - there's no separate stored residual
   *  likelihood/impact to plot a real second heat map from, only the combined residual score. */
  heatmap?: Array<{ name: string; data: number[] }>;
  /** The residual perspective's honest substitute for a heat map: how many risks fall in each
   *  real risk band once controls are accounted for, from the real persisted residual score. */
  bandDistribution?: Breakdown;
  treatmentPipeline: Breakdown;
  byCategory: Breakdown;
}

export interface ReportsPdfData {
  counts: { risks: number };
  inherent: RiskPerspectiveData;
  residual: RiskPerspectiveData;
}

const toBarItems = (b: Breakdown, tone?: string): Array<{ label: string; value: number; tone?: string }> =>
  b.labels.map((label, i) => ({ label, value: b.values[i], tone }));

const BAND_TONE: Record<string, string> = { Minimal: 'success', Low: 'success', Medium: 'warning', High: 'danger', Critical: 'danger' };

function riskPerspectiveSection(doc: Doc, heading: string, scoreLabel: string, data: RiskPerspectiveData): void {
  doc.sectionHeading(heading);

  doc.statRow([
    { label: 'Total Risks', value: String(data.total) },
    { label: 'Critical Risks', value: String(data.critical) },
    { label: `Avg ${scoreLabel} Risk`, value: String(data.avgScore) },
  ]);

  if (data.heatmap) {
    doc.card('Risk Heat Map (Likelihood × Impact)', () => {
      doc.heatmapGrid(
        data.heatmap!.map((row) => row.name.replace('Likelihood ', 'L')),
        ['I1', 'I2', 'I3', 'I4', 'I5'],
        data.heatmap!.map((row) => row.data),
      );
    });
  }

  if (data.bandDistribution) {
    doc.card('Residual Risk Distribution', () => {
      doc.barChart(data.bandDistribution!.labels.map((label, i) => ({
        label, value: data.bandDistribution!.values[i], tone: BAND_TONE[label] ?? 'primary',
      })));
    });
  }

  doc.card('Risk Treatment Pipeline', () => {
    doc.barChart(toBarItems(data.treatmentPipeline));
  });

  doc.card('Risks by Category', () => {
    doc.barChart(toBarItems(data.byCategory));
  });
}

export function buildReportsPdf(data: ReportsPdfData, opts: { now?: Date } = {}): PdfBuildResult {
  const generatedAt = (opts.now ?? new Date()).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const doc = new Doc(generatedAt, FOOTER_LABEL);
  doc.coverTitle('Nimbus Risk Overview', `${data.counts.risks} risks · generated ${generatedAt}`);

  riskPerspectiveSection(doc, 'Inherent Risk', 'Inherent', data.inherent);
  doc.pageBreak();
  riskPerspectiveSection(doc, 'Residual Risk', 'Residual', data.residual);

  const { pdf, pages, maxYByPage, overflows } = doc.finish();
  const filename = `nimbus-risk-overview-${new Date().toISOString().slice(0, 10)}.pdf`;
  return { pages, maxYByPage, overflows, filename, save: () => pdf.save(filename) };
}

export function exportReportsPdf(data: ReportsPdfData, opts: { now?: Date } = {}): void {
  buildReportsPdf(data, opts).save();
}
